import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Users, Plus, RefreshCw, Search, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { EmptyState } from '@/components/shared/EmptyState';
import { ContactDetailPanel } from '@/features/contacts/components/ContactDetailPanel';
import { ContactFormDialog } from '@/features/contacts/components/ContactFormDialog';
import type { Database } from '@/types/database';

type Contact = Database['public']['Tables']['contact']['Row'];

export function ContactsPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const { data: contacts, isLoading } = useQuery({
    queryKey: ['contacts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('contact')
        .select('*')
        .is('deleted_at', null)
        .order('name');
      if (error) throw error;
      return data as Contact[];
    },
  });

  const syncContacts = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.functions.invoke('contacts-sync', {
        body: {},
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });

  const filtered = (contacts ?? []).filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase())
    || (c.email ?? '').toLowerCase().includes(search.toLowerCase())
    || (c.company ?? '').toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="flex h-[calc(100vh-3.5rem)] flex-col">
      <PageHeader
        title="Contacts"
        description={`${contacts?.length ?? 0} contacts`}
        action={
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => syncContacts.mutate()}
              disabled={syncContacts.isPending}
            >
              {syncContacts.isPending
                ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                : <RefreshCw className="mr-2 h-4 w-4" />}
              Sync Directory
            </Button>
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus className="mr-2 h-4 w-4" /> Add Contact
            </Button>
          </div>
        }
      />

      <div className="flex flex-1 overflow-hidden">
        {/* Contact List */}
        <div className="flex-1 overflow-y-auto border-r">
          <div className="sticky top-0 z-10 bg-background p-3 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search contacts..."
                className="pl-9"
              />
            </div>
          </div>

          {isLoading ? (
            <div className="space-y-2 p-3">
              {Array.from({ length: 10 }).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={Users}
              title="No contacts"
              description="Sync from your directory or add manually"
            />
          ) : (
            filtered.map((contact) => (
              <button
                key={contact.id}
                onClick={() => setSelectedContact(contact)}
                className={`flex w-full items-center gap-3 border-b p-3 text-left hover:bg-accent/50 ${
                  selectedContact?.id === contact.id ? 'bg-accent' : ''
                }`}
              >
                <Avatar className="h-10 w-10">
                  <AvatarImage src={contact.avatar_url ?? undefined} />
                  <AvatarFallback>
                    {contact.name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{contact.name}</div>
                  <div className="text-sm text-muted-foreground truncate">
                    {contact.title ?? contact.email ?? ''}
                  </div>
                </div>
                <Badge variant="outline" className="shrink-0 text-[10px]">
                  {contact.source}
                </Badge>
              </button>
            ))
          )}
        </div>

        {/* Detail Panel */}
        {selectedContact && (
          <div className="w-96 shrink-0 overflow-y-auto">
            <ContactDetailPanel
              contact={selectedContact}
              onClose={() => setSelectedContact(null)}
            />
          </div>
        )}
      </div>

      <ContactFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
      />
    </div>
  );
}
